import { useState } from "react";
import { CheckCircle2, Upload, ShieldCheck } from "lucide-react";
import { usePage } from "@/lib/content";
import { Reveal } from "@/components/site/Motion";
import { PageHero, usePageTitle } from "@/components/site/Bits";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";

export default function FacaParte() {
  usePageTitle("Faça Parte");
  const { sections: s } = usePage("faca_parte");
  const areasInteresse = (s.areas_interesse || "").split(";").map((a) => a.trim()).filter(Boolean);

  const [form, setForm] = useState({ name: "", email: "", phone: "", area: "", presentation: "" });
  const [file, setFile] = useState(null);
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [apiError, setApiError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    const er = {};
    if (form.name.trim().length < 3) er.name = "Informe seu nome completo.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Informe um e-mail válido.";
    if (form.phone.replace(/\D/g, "").length < 8) er.phone = "Informe um telefone válido.";
    if (!form.area) er.area = "Selecione a área de interesse.";
    if (!file) er.file = "Anexe seu currículo (PDF, DOC ou DOCX, até 10 MB).";
    if (!consent) er.consent = "É necessário aceitar o aviso de privacidade.";
    return er;
  };

  const submit = async (e) => {
    e.preventDefault();
    setApiError("");
    const er = validate();
    setErrors(er);
    if (Object.keys(er).length) return;
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("file", file);
      await api.post("/applications", fd);
      setDone(true);
    } catch (err) {
      if (err.response?.status === 413) setApiError("Arquivo muito grande. Limite de 10 MB.");
      else if (err.response?.status === 422) setApiError(err.response.data?.detail || "Formato de arquivo não aceito. Envie PDF, DOC ou DOCX.");
      else setApiError(formatApiErrorDetail(err.response?.data?.detail) || "Não foi possível enviar. Tente novamente.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <PageHero title={s.hero_title || "Faça Parte"} text={s.hero_text} overline="Silva Cotan e Associados — Assessoria Jurídica" />

      <section className="bg-ivory">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-20 md:px-8 md:py-28 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="text-base leading-relaxed text-inkgray md:text-lg">{s.intro_text}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-10 border border-navy/10 bg-white p-8" data-testid="faca-parte-privacidade">
                <p className="flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.22em] text-gold-deep">
                  <ShieldCheck size={16} strokeWidth={1.5} /> Aviso de privacidade — LGPD
                </p>
                <p className="mt-4 text-sm leading-relaxed text-inkgray">{s.privacidade_text}</p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <Reveal>
                <div className="flex h-full flex-col items-center justify-center border border-gold/40 bg-white px-8 py-20 text-center" data-testid="faca-parte-success">
                  <CheckCircle2 size={44} className="text-gold-deep" strokeWidth={1.25} />
                  <p className="mt-6 font-display text-2xl text-navy md:text-3xl">Candidatura recebida com sucesso</p>
                  <p className="mt-4 max-w-md text-sm leading-relaxed text-inkgray">
                    Obrigado pelo interesse em fazer parte do time Silva Cotan. Sua candidatura e seu currículo
                    foram registrados com segurança e serão analisados com atenção.
                  </p>
                </div>
              </Reveal>
            ) : (
              <Reveal delay={0.1}>
                <form onSubmit={submit} noValidate className="border border-navy/10 bg-white p-8 md:p-10" data-testid="faca-parte-form">
                  <div className="grid gap-6 sm:grid-cols-2">
                    <Field label="Nome completo" error={errors.name}>
                      <input className={inputCls} data-testid="faca-parte-nome" value={form.name} onChange={set("name")} placeholder="Seu nome completo" />
                    </Field>
                    <Field label="E-mail" error={errors.email}>
                      <input type="email" className={inputCls} data-testid="faca-parte-email" value={form.email} onChange={set("email")} placeholder="voce@email.com" />
                    </Field>
                    <Field label="Telefone" error={errors.phone}>
                      <input className={inputCls} data-testid="faca-parte-telefone" value={form.phone} onChange={set("phone")} placeholder="(00) 00000-0000" />
                    </Field>
                    <Field label="Área de interesse" error={errors.area}>
                      <select className={inputCls} data-testid="faca-parte-area" value={form.area} onChange={set("area")}>
                        <option value="">Selecione…</option>
                        {areasInteresse.map((a) => (
                          <option key={a} value={a}>{a}</option>
                        ))}
                        <option value="Outra">Outra</option>
                      </select>
                    </Field>
                  </div>
                  <div className="mt-6">
                    <Field label="Apresentação profissional (opcional)">
                      <textarea rows={4} className={inputCls} value={form.presentation} onChange={set("presentation")} placeholder="Conte brevemente sobre sua trajetória e interesses" />
                    </Field>
                  </div>
                  <div className="mt-6">
                    <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-navy">Currículo *</p>
                    <label className="flex cursor-pointer items-center justify-between gap-4 border border-dashed border-navy/25 px-5 py-5 transition-colors hover:border-gold">
                      <span className="text-sm text-inkgray">
                        {file ? file.name : "Anexar arquivo — PDF, DOC ou DOCX (até 10 MB)"}
                      </span>
                      <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-gold-deep">
                        <Upload size={14} /> Anexar
                      </span>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        className="hidden"
                        data-testid="faca-parte-file-input"
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                      />
                    </label>
                    {errors.file && <p className="mt-2 text-xs text-red-600">{errors.file}</p>}
                  </div>
                  <label className="mt-6 flex items-start gap-3 text-sm text-inkgray">
                    <input
                      type="checkbox"
                      checked={consent}
                      data-testid="faca-parte-consent"
                      onChange={(e) => setConsent(e.target.checked)}
                      className="mt-1 h-4 w-4 shrink-0 accent-[#C9A15B]"
                    />
                    <span>
                      Li e concordo com o aviso de privacidade. Autorizo o tratamento dos meus dados para fins de recrutamento, conforme a LGPD. *
                    </span>
                  </label>
                  {errors.consent && <p className="mt-2 text-xs text-red-600">{errors.consent}</p>}
                  {apiError && <p className="mt-4 border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="faca-parte-error">{apiError}</p>}
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="faca-parte-submit"
                    className="mt-8 inline-flex items-center gap-3 bg-navy px-8 py-4 text-[11px] font-bold uppercase tracking-[0.22em] text-ivory transition-colors hover:bg-navy-2 disabled:opacity-60"
                  >
                    {sending ? "Enviando…" : "Enviar candidatura"}
                  </button>
                </form>
              </Reveal>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

const inputCls =
  "w-full border border-navy/20 bg-ivory/40 px-4 py-3.5 text-sm text-navy placeholder:text-navy/35 transition-colors focus:border-gold";

function Field({ label, children, error }) {
  return (
    <div>
      <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.18em] text-navy">{label} *</label>
      {children}
      {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
    </div>
  );
}
